import { isObject, isUndefined } from 'library/ts/utilities/modules/Type';
export class Comparator {
    settings;
    defaultSettings = {
        RECURSION_DEPTH_LIMIT: 100,
        OBJECT_NOTE_KEY: '[ COMPARISON SUMMARY ]',
        IDENTICAL_KEY: '[identical?]',
        REPORT_IDENTICALS: true,
        NOTE_IDENTICAL_OBJECTS: true,
        NOTE_DIFFERENT_OBJECTS: true,
        IGNORE_CIRCULAR_REFS: false,
        IGNORE_RIGHT_FALSE_ONLY: false,
        IGNORE_LEFT_FALSE_ONLY: false,
        IGNORE_RIGHT_UNDEFINED_ONLY: false,
        IGNORE_LEFT_UNDEFINED_ONLY: false
    };
    objectMemory;
    constructor(settings = {}) {
        this.objectMemory = new WeakSet();
        this.settings = Object.assign({}, this.defaultSettings, settings);
    }
    rememberObject = (obj) => {
        if (this.objectMemory.has(obj)) {
            return true;
        }
        this.objectMemory.add(obj);
        return false;
    };
    compareProperties = (leftProperty, rightProperty, depth = 0) => {
        if (!leftProperty && !rightProperty && leftProperty !== rightProperty) {
            return;
        }
        if (isObject(leftProperty) && isObject(rightProperty)) {
            return this.compareObjects(leftProperty, rightProperty, depth + 1);
        }
        if (isUndefined(leftProperty) && !!rightProperty) {
            if (this.settings.IGNORE_LEFT_UNDEFINED_ONLY)
                return;
            return `[! LEFT UNDEFINED ONLY: ${leftProperty} vs ${rightProperty} ]`;
        }
        if (!!leftProperty && isUndefined(rightProperty)) {
            if (this.settings.IGNORE_RIGHT_UNDEFINED_ONLY)
                return;
            return `[! RIGHT UNDEFINED ONLY: ${leftProperty} vs ${rightProperty} ]`;
        }
        if (!leftProperty && !!rightProperty) {
            if (this.settings.IGNORE_LEFT_UNDEFINED_ONLY)
                return;
            return `[! LEFT FALSE ONLY: ${leftProperty} vs ${rightProperty} ]`;
        }
        if (!!leftProperty && !rightProperty) {
            if (this.settings.IGNORE_RIGHT_FALSE_ONLY)
                return;
            return `[! RIGHT FALSE ONLY: ${leftProperty} vs ${rightProperty} ]`;
        }
        const leftType = typeof leftProperty;
        const rightType = typeof rightProperty;
        if (leftType !== rightType) {
            return `[! TYPE DIFFERENCE: ${leftType} vs ${rightType} ]`;
        }
        if (leftProperty !== rightProperty) {
            return `[! VALUE DIFFERENCE: ${leftProperty} vs ${rightProperty} ]`;
        }
        if (leftProperty === rightProperty) {
            return;
        }
        return `[! MISSED CASE: ${leftProperty} vs ${rightProperty} ]`;
    };
    compareObjects = (leftSubject, rightSubject, depth = 0) => {
        const result = {
            ...(this.settings.NOTE_IDENTICAL_OBJECTS ? { [this.settings.OBJECT_NOTE_KEY]: '[ IDENTICAL OBJECTS ]' } : {}),
            [this.settings.IDENTICAL_KEY]: 1
        };
        if (!this.settings.IGNORE_CIRCULAR_REFS) {
            const leftAlreadyRemembered = this.rememberObject(leftSubject);
            if (leftAlreadyRemembered) {
                result[this.settings.OBJECT_NOTE_KEY] = '[! CIRCULAR REFERENCE AVOIDED: LEFT REMEMBERED]';
                return result;
            }
            const rightAlreadyRemembered = this.rememberObject(rightSubject);
            if (rightAlreadyRemembered) {
                result[this.settings.OBJECT_NOTE_KEY] = '[! CIRCULAR REFERENCE AVOIDED: RIGHT REMEMBERED]';
                return result;
            }
        }
        if (depth > this.settings.RECURSION_DEPTH_LIMIT) {
            result[this.settings.OBJECT_NOTE_KEY] = '[! DEPTH LIMIT REACHED ]';
            return result;
        }
        const properties = Object.keys({
            ...(isObject(leftSubject) ? leftSubject : {}),
            ...(isObject(rightSubject) ? rightSubject : {})
        });
        let identicalProperties = 0;
        properties.forEach((k) => {
            const leftProperty = leftSubject[k];
            const rightProperty = rightSubject[k];
            const propertiesCompared = this.compareProperties(leftProperty, rightProperty, depth);
            if (!propertiesCompared || propertiesCompared?.[this.settings.IDENTICAL_KEY] === 1) {
                identicalProperties++;
                if (this.settings.REPORT_IDENTICALS) {
                    result[k] = `[ IDENTICAL: ${leftProperty} vs ${rightProperty} ]`;
                }
            }
            else {
                result[k] = propertiesCompared;
                result[this.settings.IDENTICAL_KEY] = 0;
                if (this.settings.NOTE_DIFFERENT_OBJECTS) {
                    result[this.settings.OBJECT_NOTE_KEY] = '[! DIFFERENCES EXIST ]';
                }
            }
        });
        if (identicalProperties === properties.length && !this.settings.NOTE_IDENTICAL_OBJECTS) {
            return undefined;
        }
        return result;
    };
}
